// Wave 46 — empty states with personality.
//
// Empty states used to be single-line "No X yet". The richer treatment:
//   - Editorial-gold SVG illustration (small, hand-rolled — no asset deps)
//   - Specific actionable next step
//   - Voice-aligned microcopy (Plus Jakarta body, DM Mono caption)
//
// Three preset illustrations cover the common patterns:
//   - 'inbox'      — empty list / queue / drafts
//   - 'heartbeat'  — no recent activity / events / decisions
//   - 'compass'    — guide-the-operator state (no project / position yet)
//
// Pass illustration={false} to suppress the SVG when the surface is
// space-constrained (e.g. inside a small panel).

import { UI_THEME } from '../uiTheme.js'

function InboxIllustration() {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="10"
        y="20"
        width="52"
        height="34"
        rx="6"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.5"
        fill="rgba(212, 88, 45, 0.04)"
      />
      <path
        d="M10 36 L26 36 L30 42 L42 42 L46 36 L62 36"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M22 14 L36 6 L50 14"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  )
}

function HeartbeatIllustration() {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="36"
        cy="36"
        r="26"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.5"
        fill="rgba(212, 88, 45, 0.04)"
        opacity="0.5"
      />
      <path
        d="M14 36 H22 L26 28 L34 44 L42 24 L46 36 H58"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CompassIllustration() {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="36"
        cy="36"
        r="26"
        stroke={UI_THEME.colors.brand}
        strokeWidth="1.5"
        fill="rgba(212, 88, 45, 0.04)"
      />
      <path
        d="M36 18 L42 36 L36 54 L30 36 Z"
        fill={UI_THEME.colors.brand}
        opacity="0.7"
      />
      <circle cx="36" cy="36" r="2" fill={UI_THEME.colors.bg} />
    </svg>
  )
}

const ILLUSTRATIONS = {
  inbox: InboxIllustration,
  heartbeat: HeartbeatIllustration,
  compass: CompassIllustration,
}

export function EmptyState({
  illustration = 'inbox',
  title,
  body,
  action,
  caption,
}) {
  const Illustration =
    illustration && illustration !== false
      ? ILLUSTRATIONS[illustration] || InboxIllustration
      : null
  return (
    <div role="region" data-testid="empty-state" className="studio-empty-state">
      {Illustration && (
        <div className="studio-empty-state-illustration">
          <Illustration />
        </div>
      )}
      {caption && <div className="studio-empty-state-caption">{caption}</div>}
      {title && <div className="studio-empty-state-title">{title}</div>}
      {body && <div className="studio-empty-state-body">{body}</div>}
      {action && <div className="studio-empty-state-action">{action}</div>}
    </div>
  )
}
